import React from "react";
import { Box, Button, Typography } from "@mui/material";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  errorMessage?: string;
};

function isChunkLoadError(message: string | undefined) {
  const m = String(message || "");
  return /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(m);
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: unknown) {
    // Keep logging for diagnostics; avoid crashing to a blank screen in production.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Unhandled render error:", error);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const chunkError = isChunkLoadError(this.state.errorMessage);

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 520, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {chunkError ? "A new version is available" : "Something went wrong"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {chunkError
              ? "The app updated while you were browsing. Reload to continue."
              : "Reloading usually fixes this. If it keeps happening, check the console for the underlying error."}
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Reload
          </Button>
        </Box>
      </Box>
    );
  }
}
